import React, { createContext, useState } from 'react';
import {
    getReponseById,
    updateUserSession,
    createUserSession,
    endUserSession,
    changeUserSessionStatus,
} from './requestHandler';

const SessionContext = createContext();

const initialSession = {
    userId: '',
    userRoles: [],
    userName: '',
    currentSessionId: null,
    isSmallDevice: false,
};

const initialQuestionnaireState = {
    isQuestionnaireStarted: false,
    cheminReponsesId: [],
    cheminQuestionsId: [],
    cheminFonctionner: false,
    timeStart: '',
    timeEnd: '',
    lastReponse: '',
    customReponses: [],
    pageCounter: 0,
};

const SessionContextProvider = ({ children }) => {
    const [session, setSession] = useState(initialSession);

    const [questionnaireState, setQuestionnaireState] = useState(initialQuestionnaireState);

    const getTime = () => new Date().toLocaleString();

    const setUser = (kcToken) => {
        setSession(prev => ({
            ...prev,
            userId: kcToken?.sub || '',
            userName: kcToken?.preferred_username || '',
            userRoles: kcToken?.realm_access?.roles || [],
        }));
    };

    const ajoutQuestionCheminForTracking = async (reponseId, questionId) => {
        const pageNumber = questionnaireState.cheminReponsesId.length;

        if (!session.currentSessionId) {
            console.error("Session absente : currentSessionId manquant");
            return;
        }

        if (!questionId || !reponseId) {
            console.error("questionId ou reponseId invalide", { questionId, reponseId });
            return;
        }

        const customContent = questionnaireState.customReponses.find(
            r => r.index === pageNumber
        )?.contenu || null;

        const body = {
            session_id: Number(session.currentSessionId),
            question_id: Number(questionId),
            reponse_id: Number(reponseId),
            page: Number(pageNumber),
            custom_reponse_contenu: customContent,
        };

        console.log("Tracking réponse :", body);

        await updateUserSession(body);

        setQuestionnaireState(prev => ({
            ...prev,
            cheminReponsesId: [...prev.cheminReponsesId, String(reponseId)],
            cheminQuestionsId: [...prev.cheminQuestionsId, String(questionId)],
            lastReponse: String(reponseId),
        }));
    };

    const ajoutQuestionCheminForBack = async (reponseId, questionId) => {
        const index = questionnaireState.cheminReponsesId.length - 1;

        if (index < 0 || !session.currentSessionId) return;

        const customContent = questionnaireState.customReponses.find(
            r => r.index === index
        )?.contenu || null;

        const body = {
            session_id: Number(session.currentSessionId),
            question_id: Number(questionId),
            reponse_id: Number(reponseId),
            page: Number(index),
            custom_reponse_contenu: customContent,
        };

        console.log("Tracking retour :", body);

        await updateUserSession(body);

        setQuestionnaireState(prev => {
            const newCheminReponsesId = [...prev.cheminReponsesId];
            const newCheminQuestionsId = [...prev.cheminQuestionsId];

            newCheminReponsesId[index] = String(reponseId);
            newCheminQuestionsId[index] = String(questionId);

            return {
                ...prev,
                cheminReponsesId: newCheminReponsesId,
                cheminQuestionsId: newCheminQuestionsId,
                lastReponse: String(reponseId),
            };
        });
    };

    const retirerQuestionChemin = () => {
        setQuestionnaireState(prev => {
            if (prev.cheminReponsesId.length <= 1) return prev;

            const newCheminReponsesId = [...prev.cheminReponsesId];
            const newCheminQuestionsId = [...prev.cheminQuestionsId];

            const lastReponseId = newCheminReponsesId.pop();
            newCheminQuestionsId.pop();

            return {
                ...prev,
                cheminReponsesId: newCheminReponsesId,
                cheminQuestionsId: newCheminQuestionsId,
                customReponses: prev.customReponses.filter(
                    el => el.reponse_id !== parseInt(lastReponseId, 10)
                ),
                lastReponse: newCheminReponsesId[newCheminReponsesId.length - 1] || '',
            };
        });
    };

    const addCustomReponse = (contenu, id) => {
        setQuestionnaireState(prev => {
            const pageIndex = prev.cheminReponsesId.length;
            const elementIndex = prev.customReponses.findIndex(
                element => element.index === pageIndex
            );

            const newCustomReponses = [...prev.customReponses];

            if (elementIndex === -1) {
                newCustomReponses.push({
                    contenu,
                    index: pageIndex,
                    reponse_id: id,
                });
            } else {
                newCustomReponses[elementIndex] = {
                    ...newCustomReponses[elementIndex],
                    contenu,
                };
            }

            return {
                ...prev,
                customReponses: newCustomReponses,
            };
        });
    };

    const getSpecificReponseFromState = async (index) => {
        const id = questionnaireState.cheminReponsesId[index];
        if (!id) return { contenu: '', contenu_en: '' };

        const data = await getReponseById(id);

        return {
            contenu: data?.contenu || '',
            contenu_en: data?.contenu_en || '',
        };
    };

    const checkIfStringNeedsFormatting = (str) =>
        typeof str === 'string' && /\{\d+\}/.test(str);

    const nullFormat = (val) => (val === null ? 'null' : val);

    const formatString = async (string, isFrench) => {
        if (typeof string !== 'string' || !checkIfStringNeedsFormatting(string)) {
            return string;
        }

        const matches = [...string.matchAll(/\{(\d+)\}/g)];
        let formatted = string;

        for (const match of matches) {
            const i = parseInt(match[1], 10) - 1;
            if (i < 0 || i >= questionnaireState.cheminReponsesId.length) continue;

            const response = await getSpecificReponseFromState(i);
            let content = isFrench ? response.contenu : response.contenu_en;

            if (content === '[autre]') {
                const custom = questionnaireState.customReponses.find(el => el.index === i);
                content = custom ? custom.contenu : content;
            }

            formatted = formatted.replace(match[0], nullFormat(content));
        }

        return formatted;
    };

    const recommencerQuestionChemin = async () => {
        setQuestionnaireState(initialQuestionnaireState);
        setSession(prev => ({
            ...prev,
            currentSessionId: null,
        }));
    };

    const setDebutQuestionnaire = async () => {
    if (session.currentSessionId !== null) {
        await endUserSession(session.currentSessionId);
    }

    setQuestionnaireState({
        ...initialQuestionnaireState,
        isQuestionnaireStarted: true,
        timeStart: getTime(),
    });

    console.log("session.userId =", session.userId);

    const body = {
        user_id: session.userId || "test-user"
    };

    console.log("BODY envoyé à /session/create =", body);

    const data = await createUserSession(body);

    console.log("session_id reçu du backend =", data);

    setSession(prev => ({
        ...prev,
        currentSessionId: data,
    }));
};


    const setFinQuestionnaire = async (didWork) => {
        const status =
            didWork === null ? 'Terminé' : didWork ? 'A fonctionné' : 'A échoué';

        setQuestionnaireState(prev => ({
            ...prev,
            timeEnd: getTime(),
            cheminFonctionner: status,
        }));

        if (session.currentSessionId) {
            await endUserSession(session.currentSessionId);
            await changeUserSessionStatus(session.currentSessionId, { status });
        }

        setSession(prev => ({
            ...prev,
            currentSessionId: null,
        }));
    };

    const getDevice = () => session.isSmallDevice;

    const setDevice = (bool) => {
    setSession(prev => {
        if (prev.isSmallDevice === bool) {
            return prev;
        }

        return {
            ...prev,
            isSmallDevice: bool,
               };
                  });
                      };

    const getIsQuestionnaireStarted = () => questionnaireState.isQuestionnaireStarted;
    const getUserId = () => session.userId;
    const getPageCounter = () => questionnaireState.pageCounter;

    const getCurrentParentQuestion = () =>
        questionnaireState.cheminReponsesId.length > 1
            ? questionnaireState.cheminReponsesId[questionnaireState.cheminReponsesId.length - 1]
            : '0';

    const getNewNoQuestion = () => questionnaireState.cheminQuestionsId.length;

    return (
        <SessionContext.Provider
            value={{
                ajoutQuestionCheminForTracking,
                ajoutQuestionCheminForBack,
                retirerQuestionChemin,
                formatString,
                checkIfStringNeedsFormatting,
                recommencerQuestionChemin,
                setDebutQuestionnaire,
                setFinQuestionnaire,
                setDevice,
                getDevice,
                getUserId,
                getIsQuestionnaireStarted,
                getCurrentParentQuestion,
                getNewNoQuestion,
                addCustomReponse,
                setUser,
                getPageCounter,
                session,
                questionnaireState,
            }}
        >
            {children}
        </SessionContext.Provider>
    );
};

export { SessionContextProvider, SessionContext };
